import api from './api';
import {
  TenantAdmin,
  TenantAdminCreateRequest,
  TenantAdminPagedResult,
  TenantAdminSearchParams,
  TenantAdminUpdateRequest,
  TenantAdminUserAssignmentDetail,
  TenantAdminUserAssignmentInput,
} from '../types';

const base = '/api/admin/tenants';

export interface TenantAdminAccessStatus {
  gateRequired: boolean;
}

export interface TenantAdminVerifyAccessResult {
  gateToken: string;
  expiresAt: string;
  gateRequired: boolean;
}

function normalizeTenant(raw: Record<string, unknown>): TenantAdmin {
  return {
    id: Number(raw.id),
    code: String(raw.code ?? '').trim(),
    name: String(raw.name ?? '').trim(),
    domain: String(raw.domain ?? '').trim() || undefined,
    dbConnectionString: String(raw.dbConnectionString ?? raw.db_connection_string ?? '').trim(),
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : Boolean(raw.is_active ?? true),
    plan: String(raw.plan ?? '').trim() || undefined,
    createdAt: (raw.createdAt ?? raw.created_at) as string | undefined,
    expiresAt: (raw.expiresAt ?? raw.expires_at) as string | undefined,
  };
}

function mapTenantPayload(body: TenantAdminCreateRequest | TenantAdminUpdateRequest) {
  return {
    code: body.code.trim(),
    name: body.name.trim(),
    domain: body.domain?.trim() || undefined,
    dbConnectionString: body.dbConnectionString?.trim() || undefined,
    isActive: body.isActive ?? true,
    plan: body.plan?.trim() || undefined,
    expiresAt: body.expiresAt || undefined,
  };
}

const tenantAdminService = {
  getAccessStatus: async (): Promise<TenantAdminAccessStatus> => {
    const response = await api.get(`${base}/access-status`);
    const data = response.data as Record<string, unknown>;
    return {
      gateRequired: Boolean(data.gateRequired ?? data.gate_required ?? false),
    };
  },

  verifyAccess: async (password: string): Promise<TenantAdminVerifyAccessResult> => {
    const response = await api.post(`${base}/verify-access`, { password });
    const data = response.data as Record<string, unknown>;
    return {
      gateToken: String(data.gateToken ?? data.gate_token ?? ''),
      expiresAt: String(data.expiresAt ?? data.expires_at ?? ''),
      gateRequired: Boolean(data.gateRequired ?? data.gate_required ?? true),
    };
  },

  listPaged: async (params: TenantAdminSearchParams): Promise<TenantAdminPagedResult> => {
    const response = await api.get(base, { params });
    const data = response.data;
    const pageNumber = params.pageNumber || 1;
    const pageSize = params.pageSize || 10;

    if (Array.isArray(data)) {
      const items = data.map((row) => normalizeTenant(row as Record<string, unknown>));
      return {
        items,
        totalCount: items.length,
        pageNumber,
        pageSize,
        totalPages: Math.ceil(items.length / pageSize) || 0,
      };
    }

    if (data && typeof data === 'object' && 'items' in data) {
      const paged = data as TenantAdminPagedResult;
      return {
        ...paged,
        items: (paged.items as unknown[]).map((row) =>
          normalizeTenant(row as Record<string, unknown>),
        ),
      };
    }

    return {
      items: [],
      totalCount: 0,
      pageNumber,
      pageSize,
      totalPages: 0,
    };
  },

  getById: async (id: number): Promise<TenantAdmin> => {
    const response = await api.get(`${base}/${id}`);
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  create: async (body: TenantAdminCreateRequest): Promise<TenantAdmin> => {
    const response = await api.post(base, mapTenantPayload(body));
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  update: async (id: number, body: TenantAdminUpdateRequest): Promise<TenantAdmin> => {
    const response = await api.put(`${base}/${id}`, mapTenantPayload(body));
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  setActiveStatus: async (id: number, isActive: boolean): Promise<TenantAdmin> => {
    const response = await api.patch(`${base}/${id}/status`, { isActive });
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  getTenantUsers: async (tenantId: number): Promise<TenantAdminUserAssignmentDetail[]> => {
    const response = await api.get(`${base}/${tenantId}/users`);
    const data = response.data;
    if (!Array.isArray(data)) return [];
    return data.map((row) => normalizeTenantUser(row as Record<string, unknown>));
  },

  setTenantUsers: async (
    tenantId: number,
    users: TenantAdminUserAssignmentInput[],
  ): Promise<TenantAdminUserAssignmentDetail[]> => {
    const response = await api.put(`${base}/${tenantId}/users`, {
      users: users.map((user) => ({
        userId: user.userId,
        role: user.role.trim() || 'User',
        isDefault: user.isDefault,
      })),
    });
    const data = response.data;
    if (!Array.isArray(data)) return [];
    return data.map((row) => normalizeTenantUser(row as Record<string, unknown>));
  },
};

function normalizeTenantUser(raw: Record<string, unknown>): TenantAdminUserAssignmentDetail {
  return {
    userId: Number(raw.userId ?? raw.user_id),
    username: String(raw.username ?? '').trim(),
    displayName: String(raw.displayName ?? raw.display_name ?? '').trim() || undefined,
    email: String(raw.email ?? '').trim() || undefined,
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : Boolean(raw.is_active ?? true),
    role: String(raw.role ?? 'User').trim() || 'User',
    isDefault: raw.isDefault !== undefined ? Boolean(raw.isDefault) : Boolean(raw.is_default ?? false),
  };
}

export default tenantAdminService;
